<template>
  <div>
    <LockLoader :text="loaderText" v-if="loading"/>

    <div v-else id="confirmation-vtex">
      <div
        class="header"
        :style="{ backgroundColor: '#' + retailerColor || 'var(--primary)' }"
      >
        <div class="nav-logo-wrapper">
          <Asset
            :name="retailerLogo || 'logo-white.svg'"
            alt="Logo"
            title="Logo"
            :version="null"
          />
        </div>
      </div>
      <div v-show="false">
        <NextStepsV2 />
      </div>
    </div>
  </div>
</template>

<script>
import querystring from 'querystring';
import axios from 'axios';
import { mapState, mapGetters, mapActions } from 'vuex';
import debug from 'debug';
import StringMixin from '@/mixins/string';
import UtilMixin from '@/mixins/util';
import LockLoader from '@/components/common/LockLoader.vue';
import Logger from '@/lib/logger';
import Asset from '@/components/common/Asset.vue';
import globals from '@/globals';
import StoreApi from '../../../services/store';
import NextStepsV2 from '../../onboarding/components/NextStepsV2.vue';

const log = debug('vue:checkout:summary');

export default {
  name: 'checkout-vtex-confirmation',
  metaInfo: {
    title: 'Finalização',
  },
  data: () => ({
    loaderText: 'Carregando seu pedido no Parcelex',
    loading: true,
    confirmUrl: null,
    retailerColor: null,
    retailerLogo: null,
  }),
  mixins: [StringMixin, UtilMixin],
  components: {
    LockLoader,
    Asset,
    NextStepsV2,
  },
  methods: {
    ...mapActions('checkout', [
      'resetLoading',
      'checkoutValidate',
      'reload',
      'emitEvent',
    ]),
    ...mapActions('onboarding', ['getOnboarding']),
    ...mapActions('auth', ['setToken']),
    async handleFinishTransaction() {
      const optionId = this.installmentSelected._id;
      try {
        const query = querystring.stringify({ optionId });
        const { data } = await axios.get(`${this.confirmUrl}?${query}`);
        const { transactionId, onboardingToken } = data;

        log('query', transactionId, onboardingToken);
        await this.setToken({ token: onboardingToken });
        window.location.href = `${globals().onboardingUrl}/?onboardingToken=${onboardingToken}&transactionId=${transactionId}`;
        await this.getOnboarding({ transactionId, onboardingToken });
      } catch (err) {
        Logger.error(err);
        this.setToken({ token: null });
        this.$router.replace({ name: 'checkout-error' });
      }
    },
  },
  computed: {
    ...mapState('checkout', ['user', 'order', 'checkoutId']),
    ...mapGetters('checkout', ['installmentSelected']),
    ...mapState('onboarding', [
      'name',
      'phone',
      'email',
      'zipCode',
      'city',
      'state',
      'address',
      'orderId',
      'createdAt',
      'downPaymentValue',
      'downPaymentPercentage',
      'totalAmount',
      'numberOfInstallments',
      'paymentInstallments',
      'interest',
    ]),
    showProductListMobile() {
      return this.isMobile() && this.order && this.order.products;
    },
  },
  mounted() {
    const { checkoutId, confirmUrl, retailerColor, retailerLogo } = this.$route.query;
    this.confirmUrl = confirmUrl;
    this.retailerColor = retailerColor && retailerColor;
    this.retailerLogo = retailerLogo && retailerLogo.split('/').pop();
    this.checkoutValidate(checkoutId)
      .then(async () => {
        await this.handleFinishTransaction();
        this.emitEvent({
          type: 'show',
          page: 'thank-you',
        });
      })
      .catch(async () => {
        if (checkoutId) {
          const { data } = await StoreApi.get(`/store/${checkoutId}`);
          this.reload(data);
          await this.handleFinishTransaction();
          this.emitEvent({
            type: 'show',
            page: 'thank-you',
          });
        } else {
          throw new Error('checkoutId not defined');
        }
      })
      .catch((err) => {
        const info = `checkoutId: ${checkoutId}, this.checkoutId ${this.checkoutId}`;
        log(info);
        Logger.error(err, info);
        this.$router.replace({ path: 'vtex/error' });
      })
      .finally(() => {
        this.loading = false;
      });
  },
};
</script>

<style lang="scss" scoped>
  .header {
    display: flex;
    justify-content: center;
    height: 50px;

    background-color: var(--primary);

    img {
      height: 32px;
      width: auto;
      display: flex;
      align-self: center;
    }

    @media (max-width: $md) {
      justify-content: center;
      padding: 15px 0;
      height: 20px;
    }
  }
  .nav-logo-wrapper {
    max-width: 964px;
    width: 100%;
    display: flex;
  }
  .nav-logo-wrapper img {
    padding: 20px;
  }
  #confirmation-page {
    width: 100%;
    display: flex;
    justify-content: center;

    background-color: var(--gray-100);
    min-height: 100vh;
    box-sizing: border-box;
    padding-top: 24px;

    @media (max-width: $md) {
      background-color: var(--white);
      flex-direction: column;
      padding-top: 0;
    }
  }

  .info-box {
    @media (min-width: $md) {
      padding-right: 20px;
      width: 676px;
      height: fit-content;
    }
  }

  .info-inner-box {
    border: 1px solid var(--gray-300);
    padding: 16px 20px;
    background: var(--white);
    border-radius: 8px;

    @media (max-width: $md) {
      margin-top: 0px;
      border-radius: 0px;
      border: 0px;
      padding: 20px 24px;
    }
  }

  .extra-info {
    width: 328px;

    @media (max-width: $md) {
      margin: 24px;
      width: auto;
    }
  }

  .product-box-title {
    display: flex;
    justify-content: space-between;
    color: var(--gray-700);
    margin-bottom: 8px;

    @media (max-width: $md) {
      padding: 0;
      position: relative;

      h4 {
        font-size: 20px;
        line-height: 24px;
      }
    }
  }

  .address-box {
    h4 { margin: 16px 0 8px 0; }

    @media (max-width: $md) {
      h4 { margin-top: 20px; }
    }
  }

  .payment-box {
    margin-top: 24px;

    .payment-box-row {
      display: flex;
      justify-content: space-between;
    }

    .payment-box-title {
      @extend .payment-box-row;
      margin-bottom: 8px;

      @media (max-width: $md) {
        margin-top: 20px;
      }
    }

    .payment-box-info {
      .payment-box-item {
        padding: 12px 0;

        .payment-box-item-info {
          @extend .x-small-regular;
          text-align: right;
        }

        &:nth-child(n) {
          border-bottom: 1px solid var(--gray-200);
        }

        &:last-child() {
          border: 0;
        }
      }
    }
  }

  .confirmation-card {
    color: var(--gray-700);

    p {
      @extend .small-regular;
      margin-top: 8px;
    }

    .confirmation-card-row {
      display: flex;
      justify-content: space-between;

      @media (max-width: $md) {
        flex-direction: column-reverse;
        gap: 20px;

        img { width: 45%; }
      }
    }

    .confirmation-card-title {
      display: flex;

      h2 { margin-right: 8px; }

      img { width: 19px; }
    }
  }

  .next-steps {
    h3 { margin: 20px 0 17px 0; }

    @media (max-width: $md) {
      margin: 0 24px;

      h3 { margin: 20px 0 16px 0 }
    }
  }

  .product-list-wrapper {
    @media (max-width: $md) {
      margin: -8px -4px;
    }
  }
</style>
